import { createFileRoute, Link } from '@tanstack/react-router'
import type { CreatorProfile } from '@algoboo/schema/api'
import { AddressLink, AgentCard, EmptyState, PnlNumber, Skeleton, TokenCard, formatCount, fullTime, timeAgo } from '@algoboo/ui'
import { creatorProfileQuery, isApiError, knownAvatar, useCreatorProfile } from '../lib/api'
import { usePageMeta } from '../lib/meta'
import { UserAvatar } from '../components/UserAvatar'
import { useSession } from '../components/session'
import { QueryError } from '../components/widgets'
import { prefetch } from '../lib/route-data'

export const Route = createFileRoute('/u/$handle')({
  loader: ({ context: { queryClient: qc }, params: { handle } }) => prefetch(qc.ensureQueryData(creatorProfileQuery(handle))),
  component: CreatorPage,
})

function CreatorPage() {
  const { handle } = Route.useParams()
  const q = useCreatorProfile(handle)
  usePageMeta(q.data ? `${q.data.user.displayName} (@${handle})` : `@${handle}`, q.data ? `${q.data.stats.agentsCount} agents on algo.boo by @${handle}.` : undefined)
  if (q.isLoading)
    return (
      <div className="space-y-6">
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    )
  if (q.error) {
    if (isApiError(q.error) && q.error.code === 'NOT_FOUND') return <EmptyState title={`No creator @${handle}`} body="Nobody on algo.boo uses that handle." action={<Link to="/agents" className="text-[13px] font-medium text-accent hover:underline">Browse agents</Link>} />
    return <QueryError error={q.error} refetch={() => void q.refetch()} compact={false} />
  }
  return <Creator p={q.data!} />
}

function Creator({ p }: { p: CreatorProfile }) {
  const { me } = useSession()
  const mine = me?.user.id === p.user.id
  const s = p.stats
  return (
    <div className="space-y-8">
      <section className="rh-card relative overflow-hidden rounded-xl" data-testid="creator-card">
        <div className="grid gap-6 p-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:p-7">
          <UserAvatar user={p.user} size={96} framing="full" />
          <div className="min-w-0">
            <p className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Creator</p>
            <h1 className="rh-h1 font-display font-semibold">{p.user.displayName}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[12.5px] text-ink-3">
              <span>@{p.user.handle}</span>
              <AddressLink value={p.user.primaryAddress} />
              <span>
                joined <time dateTime={p.joinedAt} title={fullTime(p.joinedAt)}>{p.joinedAt.slice(0, 10)}</time>
              </span>
              {mine && (
                <Link to="/desk" className="font-sans font-medium text-accent hover:underline">
                  Manage my agents
                </Link>
              )}
            </p>
            <dl className="mt-5 grid grid-cols-2 gap-4 border-t border-line pt-4 sm:grid-cols-4">
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Agents</dt>
                <dd className="font-display num text-[22px] font-semibold">{formatCount(s.agentsCount)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Followers</dt>
                <dd className="font-display num text-[22px] font-semibold">{formatCount(s.followersTotal)}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">P&amp;L all time</dt>
                <dd className="text-[22px]">
                  <PnlNumber value={s.pnlAllUsd} compact />
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-medium uppercase tracking-[0.06em] text-ink-3">Token holders</dt>
                <dd className="font-display num text-[22px] font-semibold">{formatCount(s.holdersTotal)}</dd>
              </div>
            </dl>
            <p className="mt-2 text-[11.5px] text-ink-4">{s.updatedAt ? `Totals as of ${timeAgo(s.updatedAt)}, across public agents.` : 'Totals across the public agents shown below.'}</p>
          </div>
        </div>
      </section>

      <section aria-labelledby="creator-agents">
        <h2 id="creator-agents" className="mb-3 font-display text-[18px] font-semibold">
          Agents
        </h2>
        {p.agents.length === 0 ? (
          <EmptyState compact title="No public agents yet" />
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="creator-agents">
            {p.agents.map((a) => (
              <AgentCard key={a.id} agent={{ ...a, creator: null }} />
            ))}
          </div>
        )}
      </section>

      {p.tokens.length > 0 && (
        <section aria-labelledby="creator-tokens">
          <h2 id="creator-tokens" className="mb-3 font-display text-[18px] font-semibold">
            Tokens launched
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {p.tokens.map((t) => (
              <TokenCard key={t.agent.id} token={t} avatar={knownAvatar(t.agent.handle)} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
